<template>
  <Modal id="expense-modal" :title="$t('yearly_expense_modal_title')">
    <p class="mb-0" v-html="tl('yearly_expense_modal_text_before_list')"></p>

    <ul>
      <li v-html="tl('yearly_expense_modal_text_li_0')"></li>
      <li v-html="tl('yearly_expense_modal_text_li_1')"></li>
      <li v-html="tl('yearly_expense_modal_text_li_2')"></li>
    </ul>

    <p v-html="tl('yearly_expense_modal_text_after_list')"></p>

    <Collapsible :text-collapsed="$t('ctr_thresholds_button_show')" :text-open="$t('ctr_thresholds_button_hide')">
      <b>{{ $t('ctr_thresholds_title') }}</b>
      <table>
        <tr v-for="(threshold, index) of getCurrentCtrThresholdPeriod().thresholds">
          <td v-if="index === 0">
            Under {{ $n(threshold.max, 'currency') }}
          </td>
          <td v-else-if="index === getCurrentCtrThresholdPeriod().thresholds.length - 1">
            Over {{ $n(threshold.min, 'currency') }}
          </td>
          <td v-else>
            {{ $n(threshold.min, 'currency') }} - {{ $n(threshold.max, 'currency') }}
          </td>
          <td>{{ threshold.factor * 100 }}% tilskud</td>
        </tr>
      </table>
    </Collapsible>

  </Modal>

  <Modal id="subsidiary-modal" :title="$t('health_subsidiary_modal_title')">
    <p v-html="tl('health_subsidiary_modal_text')"></p>
  </Modal>

  <Modal id="insurance-modal" :title="$t('insurance_modal_title')">
    <p v-html="tl('insurance_modal_text')"></p>
  </Modal>

  <div class="container">
    <div class="row">
      <div class="col mt-3 mb-4" :class="{'mt-5': !appWebview}">
        <h1 v-if="!appWebview">{{ $t('header_title') }}</h1>
        <p v-html="$t('header_text')"></p>
      </div>
    </div>

    <div class="row gx-0 gx-xl-5 gy-4 mb-4 flex-row-reverse">
      <div class="info-box col-12 col-xl-3 p-4">
        <h2>{{ $t('info_box_title') }}</h2>
        <p class="p-1" v-html="$t('info_box_text')"></p>
        <a class="btn btn-primary w-100" :href="$t('info_box_button_link')"
           target="_blank">
          {{ $t('info_box_button_text') }}
        </a>
      </div>

      <div class="calculator col-12 col-xl-9">
        <div class="row gx-0 gy-4 gy-md-0">
          <form class="calc-input col-12 col-md-6 p-0 p-md-4">
            <div class="row">
              <div class="col">
                <h3>{{ $t('yearly_expense_title') }}&nbsp;</h3>
                <div class="input-group input-group-lg">
                  <input class="form-control" type="number" min="0" v-model="yearlyExpense" required
                         :class="{'is-invalid': yearlyExpenseInvalid}" ref="yearlyExpenseInput"
                         @keyup.enter="$refs.healthSubsidiaryInput.focus"
                         @keypress="isNumber($event)"
                         :aria-label="$t('yearly_expense_alt_label')" aria-describedby="yearly-expense">
                  <span class="input-group-text" id="yearly-expense">kr.</span>
                  <span class="invalid-feedback">{{ $t('input_invalid_label') }}&nbsp;</span>
                </div>
                <div class="info-text clickable" data-bs-toggle="modal" data-bs-target="#expense-modal">
                  <img class="me-1" src="@/assets/info.svg" :alt="$t('input_help_alt_label')" width="28" height="28">
                  <span>{{ $t('input_help_label') }}&nbsp;</span>
                </div>
              </div>
            </div>
            <div class="row">
              <div class="col">
                <h3>{{ $t('health_subsidiary_title') }}&nbsp;</h3>
                <div class="input-group input-group-lg">
                  <input class="form-control" type="number" min="0" max="85" v-model="healthSubsidiary" required
                         :class="{'is-invalid': healthSubsidiaryInvalid}" ref="healthSubsidiaryInput"
                         @keyup.enter="$refs.insuranceInput.focus"
                         @keypress="isNumber($event)"
                         :aria-label="$t('health_subsidiary_alt_label')" aria-describedby="health-subsidiary">
                  <span class="input-group-text" id="health-subsidiary">%</span>
                  <span class="invalid-feedback">{{ $t('input_invalid_label') }}&nbsp;</span>
                </div>
                <div class="info-text clickable" data-bs-toggle="modal" data-bs-target="#subsidiary-modal">
                  <img class="me-1" src="@/assets/info.svg" :alt="$t('input_help_alt_label')" width="28" height="28">
                  <span>{{ $t('input_help_label') }}&nbsp;</span>
                </div>
              </div>
            </div>
            <div class="row">
              <div class="col">
                <h3>{{ $t('insurance_title') }}&nbsp;</h3>
                <select class="form-select form-select-lg" v-model="insurance" required
                        :class="{unselected: insurance === null}" ref="insuranceInput"
                        :aria-label="$t('insurance_alt_label')" aria-required="true">
                  <option value="NONE" hidden selected>{{ $t('insurance_option_null') }}</option>
                  <option value="NOT_MEMBER">{{ $t('insurance_option_not_member') }}</option>
                  <option value="INACTIVE_MEMBER">{{ $t('insurance_option_inactive_member') }}</option>
                  <option value="GROUP_1">{{ $t('insurance_option_group_1') }}</option>
                  <option value="GROUP_2">{{ $t('insurance_option_group_2') }}</option>
                  <option value="GROUP_5">{{ $t('insurance_option_group_5') }}</option>
                </select>
                <div class="info-text clickable" data-bs-toggle="modal" data-bs-target="#insurance-modal">
                  <img class="me-1" src="@/assets/info.svg" :alt="$t('input_help_alt_label')" width="28" height="28">
                  <span>{{ $t('input_help_label') }}&nbsp;</span>
                </div>
              </div>
            </div>
          </form>

          <div class="calc-result col-12 col-md-6 p-4">
            <div class="empty-state"
                 v-if="yearlyExpense === null || healthSubsidiary === null || insurance === 'NONE' || yearlyExpenseInvalid || healthSubsidiaryInvalid">
              <img class="mb-4" src="@/assets/dosis.svg" :alt="$t('empty_state_icon_alt')" width="125" height="132">
              <p>
                <b>
                  {{ $t('empty_state_text') }}
                </b>
              </p>
            </div>

            <div class="results" ref="results" v-else
                 v-observe-visibility="{callback: resultVisibilityChanged, intersection: {threshold: 0.20}}">
              <div class="row">
                <span class="result-header">{{ $t('expense_daily_label') }}</span>
                <span class="result-text">{{ $n(daily, 'currencyDecimal') }}</span>
              </div>
              <hr>
              <div class="row">
                <span class="result-header">{{ $t('expense_fortnightly_label') }}</span>
                <span class="result-text">{{ $n(fortnightly, 'currencyDecimal') }}</span>
              </div>
              <hr>
              <div class="row">
                <span class="result-header">{{ $t('expense_yearly_label') }}</span>
                <span class="result-text">{{ $n(yearly, 'currencyDecimal') }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="row">
      <p class="disclaimer">
        * {{ $t('disclaimer_text') }}
      </p>
    </div>

    <transition name="slide">
      <div class="scroll-to-prices position-fixed bottom-0 start-50 translate-middle-x" v-if="showScrollButton">
        <div class="info-text clickable" @click="$refs.results.scrollIntoView({block: 'center', inline: 'center'})">
          <span>{{ $t('scroll_to_prices_text') }}&nbsp;</span>
          <img class="me-1" src="@/assets/chevron-down.svg" :alt="$t('scroll_to_prices_text')">
        </div>
      </div>
    </transition>
  </div>
</template>

<script lang="ts">
import {defineComponent} from 'vue';
import Modal from "@/components/Modal.vue";
import Collapsible from "@/components/Collapsible.vue";
import {
  CTR_THRESHOLDS,
  CtrThresholdPeriod,
  DOSE_PRICE_YEARLY,
  EXPENSE_FACTOR,
  Insurance,
  INSURANCE_REIMBURSEMENT_FACTORS,
} from "@/constants";

export default defineComponent({
  name: 'DosePrice',
  components: {Collapsible, Modal},
  props: [
    'appWebview'
  ],
  data: () => {
    return {
      yearlyExpense: null as null | number,
      healthSubsidiary: null as null | number,
      insurance: 'NONE' as Insurance,
      showScrollButton: false
    };
  },
  mounted() {
    const urlParams = new URLSearchParams(window.location.search);
    const yearlyExpenseParam = urlParams.get('forventet-forbrug');
    if (yearlyExpenseParam != null) {
      this.yearlyExpense = parseInt(yearlyExpenseParam);
    }

    const healthSubsidiaryParam = urlParams.get('helbredstillaeg');
    if (healthSubsidiaryParam != null) {
      this.healthSubsidiary = parseInt(healthSubsidiaryParam);
    }

    if (this.yearlyExpense === null) {
      (this.$refs.yearlyExpenseInput as HTMLInputElement).focus();
    } else if (this.healthSubsidiary === null) {
      (this.$refs.healthSubsidiaryInput as HTMLInputElement).focus();
    }
  },
  computed: {
    yearlyExpenseInvalid(): boolean {
      if (this.yearlyExpense == null) {
        return false;
      }

      return this.yearlyExpense < 0;
    },
    healthSubsidiaryInvalid(): boolean {
      if (this.healthSubsidiary == null) {
        return false;
      }

      return this.healthSubsidiary < 0 || this.healthSubsidiary > 85;
    },
    yearly(): number {
      return Math.abs(this.calculateYearlyExtra());
    },
    fortnightly(): number {
      return Math.abs(this.yearly / 26);
    },
    daily(): number {
      return Math.abs(this.yearly / 365);
    }
  },
  methods: {
    isNumber(evt: KeyboardEvent): void {
      const keysAllowed: string[] = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '.', ','];
      const keyPressed: string = evt.key;

      if (!keysAllowed.includes(keyPressed)) {
        evt.preventDefault()
      }
    },
    tl(path: string) {
      return this.expandLinks(this.$t(path));
    },
    expandLinks(input: string): string {
      const regex = /\[([^\]]*)]\(([^)]*)\)/gm;
      const linkSubstitution = `<a class="link-primary" href="$2" target="_blank">$1</a>`;

      return input.replace(regex, linkSubstitution);
    },
    calculateYearlyExtra(): number {
      if (this.yearlyExpense == null) {
        return 0;
      }
      const withoutDose = this.subtractReimbursements(this.yearlyExpense);
      const withDose = this.subtractReimbursements((this.yearlyExpense * EXPENSE_FACTOR) + DOSE_PRICE_YEARLY)

      return withDose - withoutDose;
    },
    subtractReimbursements(gross: number): number {
      let net = gross;

      const ctrReimbursement = this.calculateCtrReimbursement(net);
      net -= ctrReimbursement;

      const healthSubsidiaryReimbursement = this.calculateHealthSubsidiaryReimbursement(net);
      net -= healthSubsidiaryReimbursement;

      const insuranceReimbursement = this.calculateInsuranceReimbursement(net);
      net -= insuranceReimbursement;

      return Math.max(0, net);
    },
    calculateCtrReimbursement(gross: number): number {
      return this.getCurrentCtrThresholdPeriod().thresholds
          .map(value => gross < value.min ? 0 : (Math.min(value.max, gross) - value.min) * value.factor)
          .reduce((sum, value) => sum + value, 0);
    },
    getCurrentCtrThresholdPeriod(): CtrThresholdPeriod {
      const now = new Date();

      const currentPeriod: CtrThresholdPeriod | undefined = CTR_THRESHOLDS
          .find(value => value.validFrom <= now && now < value.validTo);

      if (currentPeriod === undefined) {
        throw new Error("Missing a valid ctr period");
      }

      return currentPeriod;
    },
    calculateHealthSubsidiaryReimbursement(net: number): number {
      if (this.healthSubsidiary == null) {
        return 0;
      }

      return net * (this.healthSubsidiary / 100);
    },
    calculateInsuranceReimbursement(net: number): number {
      return net * INSURANCE_REIMBURSEMENT_FACTORS[this.insurance]
    },
    resultVisibilityChanged(visible: boolean, entry: any) {
      this.showScrollButton = !visible && entry.boundingClientRect.y > 0;
    }
  }
});
</script>

<style scoped lang="scss">
.calculator {
  border-radius: 8px;

  .calc-input {
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: space-around;
    border: 4px solid $color-white;
    border-radius: 8px;
    background-color: $color-white;
    box-shadow: 0 3px 8px 0 rgb(32 91 101 / 0.2);

    .row {
      margin: 16px 0;

      h3 {
        margin-bottom: 12px;

        &::after {
          content: '*';
          color: $color-danger;
        }
      }

      .input-group, .form-select {
        margin-bottom: 16px;
      }

      .form-select {
        &.unselected {
          color: $color-november;
        }

        option {
          color: $color-dark;
        }
      }

      .input-group {
        .form-control {
          padding-right: 0.5rem;
        }

        .input-group-text {
          width: 56px;
          justify-content: center;
          border-top-right-radius: 4px;
          border-bottom-right-radius: 4px;
        }
      }

      .form-control.is-invalid {
        background-image: none;

        & + .input-group-text {
          border-color: $color-danger;
        }
      }

      .info-text img {
        vertical-align: bottom;
      }
    }
  }

  .calc-result {
    border: 4px solid $color-white;
    border-radius: 8px;
    background-color: $color-haze;
    box-shadow: 0 3px 8px 0 rgb(32 91 101 / 0.2);

    & > div {
      height: 100%;
      display: flex;
      flex-direction: column;
    }

    .empty-state {
      justify-content: center;
      align-items: center;

      p {
        max-width: 250px;
        text-align: center;
      }
    }

    .results {
      justify-content: space-evenly;
      text-align: center;

      hr {
        height: 1px;
        color: $color-brand;
      }

      .result-header {
        font-size: 18px;
        font-weight: 600;
      }

      .result-text {
        font-family: 'Pharma Bold', sans-serif;
        font-size: 40px;
        font-weight: 700;
      }
    }
  }
}

@media (min-width: 768px) {
  .calculator {
    & > .row {
      height: 100%;
      box-shadow: 0 3px 8px 0 rgb(32 91 101 / 0.2);
      border-radius: 8px;
    }

    .calc-input {
      border-top: 4px solid $color-white;
      border-right: none;
      border-bottom: 4px solid $color-white;
      border-left: 4px solid $color-white;

      border-radius: 8px 0 0 8px;
      box-shadow: none;

      &::after {
        display: block;
        content: '';
        position: absolute;
        top: -4px;
        right: 0;
        width: 2px;
        height: calc(100% + 8px);
        box-sizing: border-box;
        border-right: 2px solid $color-bg;
      }
    }

    .calc-result {
      border: 4px solid $color-white;
      border-radius: 0 8px 8px 0;
      background-color: $color-haze;
      box-shadow: none;
    }
  }
}

.info-box {
  border-radius: 8px;
  background-color: $color-haze;
}

.disclaimer {
  font-size: 14px;
}

.scroll-to-prices {
  width: 100%;
  height: 48px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  border-radius: 12px 12px 0 0;
  background-color: $color-white;
  box-shadow: 0px -2px 6px rgba(32, 91, 101, 0.15);
}

.slide-enter-active,
.slide-leave-active {
  transition: all 0.1s cubic-bezier(1, 0.5, 0.8, 1);
}

.slide-enter-from,
.slide-leave-to {
  height: 0
}
</style>
